import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Check, Droplets, KeyRound, Zap } from "lucide-react";
import { getCategory, prestationList, REVIEWS, prestaImage } from "../lib/prestations";
import { COMPANY, TRUST_POINTS } from "../lib/data";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import BrandMarquee from "../components/BrandMarquee";
import GoogleReviews from "../components/GoogleReviews";
import GoogleMap from "../components/GoogleMap";
import InfoStrip from "../components/InfoStrip";
import QuoteForm from "../components/QuoteForm";

const ICONS = { Droplets, KeyRound, Zap };

export default function CategoryPage() {
  const { category } = useParams();
  const cat = getCategory(category);
  useEffect(() => { window.scrollTo(0, 0); }, [category]);

  if (!cat) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center text-center px-5">
        <h1 className="font-display font-extrabold text-ink text-4xl">Catégorie introuvable</h1>
        <Link to="/" className="btn-primary mt-8">Retour à l&apos;accueil</Link>
      </main>
    );
  }

  const Icon = ICONS[cat.icon] || Droplets;
  const pages = prestationList(category);

  return (
    <main data-testid={`category-${category}`} className="bg-white">
      <Seo path={`/${category}`} title={cat.name + " d'urgence 24/7"} description={cat.intro} image={cat.image} />

      {/* Hero */}
      <section className="relative pt-28 md:pt-32 pb-14 md:pb-20 overflow-hidden bg-slate-50 border-b border-slate-200">
        <div className="pointer-events-none absolute -top-16 right-0 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <nav className="flex items-center gap-2 text-sm text-ink-muted mb-5" aria-label="Fil d'ariane">
              <Link to="/" className="hover:text-brand">Accueil</Link>
              <span>/</span>
              <span className="text-ink font-medium">{cat.name}</span>
            </nav>
            <span className="chip"><Icon className="h-4 w-4" /> {cat.tagline}</span>
            <h1 className="mt-4 font-display font-extrabold text-ink text-4xl md:text-6xl leading-[1.05]">
              {cat.name} <span className="gradient-text">d&apos;urgence 24/7</span>
            </h1>
            <p className="mt-5 text-lg text-ink-soft leading-relaxed max-w-xl">{cat.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={COMPANY.phoneHref} data-testid="category-call-btn" className="btn-primary">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
              <a href="#devis" className="btn-outline">Devis gratuit</a>
            </div>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {TRUST_POINTS.map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-ink-soft font-medium">
                  <span className="h-5 w-5 rounded-full bg-brand-green/15 flex items-center justify-center">
                    <Check className="h-3 w-3 text-brand-green" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[1.75rem] overflow-hidden border border-slate-200 shadow-xl shadow-slate-200/60 aspect-[4/3]"
          >
            <img src={cat.image} alt={`${cat.name} Le Bon Dépanneur`} fetchPriority="high" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      <BrandMarquee category={category} />

      {/* Prestations grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="overline">Nos prestations {cat.name.toLowerCase()}</span>
            <h2 className="mt-3 font-display font-extrabold text-ink text-3xl md:text-5xl">
              Une page dédiée à chaque besoin
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pages.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <Link to={`/${category}/${p.slug}`} data-testid={`prestation-card-${p.slug}`}
                  className="card-soft group block overflow-hidden h-full">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={prestaImage(p.slug)} alt={p.h1} loading="lazy" decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-ink text-lg leading-snug">{p.h1}</h3>
                    <span className="mt-3 btn-link text-brand text-sm">
                      En savoir plus <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-slate-50 border-y border-slate-200 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="overline">Notre méthode</span>
            <h2 className="mt-3 font-display font-extrabold text-ink text-3xl md:text-5xl">
              Simple, Rapide, Transparent
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {cat.process.map((st, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="card-soft p-7 h-full hover:translate-y-0">
                  <span className="font-display font-extrabold text-4xl gradient-text">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display font-bold text-ink text-xl mt-3">{st.t}</h3>
                  <p className="text-ink-soft mt-2 leading-relaxed">{st.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <GoogleReviews reviews={REVIEWS[category]} title={`Avis clients — ${cat.name}`}
        subtitle="Ils nous ont fait confiance pour leur dépannage." />

      {/* Map + info + form */}
      <section className="bg-slate-50 border-t border-slate-200 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="mb-10"><InfoStrip /></div>
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <GoogleMap className="min-h-[360px]" />
            <div id="devis" className="card-soft p-6 md:p-8 hover:translate-y-0 scroll-mt-24">
              <h2 className="font-display font-bold text-ink text-2xl mb-1">Demander un devis gratuit</h2>
              <p className="text-ink-soft mb-5">Réponse rapide, sans engagement.</p>
              <QuoteForm defaultService={category} source={`categorie-${category}`} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
